import React, { useState } from "react";
import portrait from "./assets/rf.jpg";
import {
  Entry,
  articles,
  books,
  conferences,
  contributions,
  email,
  film,
  logos,
  profiles,
  publications,
  radio,
  talks,
  teaching,
} from "./content";

const nav = [
  ["research", "Research"],
  ["books", "Books"],
  ["articles", "Articles"],
  ["film-radio", "Film and radio"],
  ["about", "About"],
];

const External: React.FC<{ href: string; children: React.ReactNode }> = ({
  href,
  children,
}) => (
  <a href={href} target="_blank" rel="noreferrer">
    {children}
  </a>
);

const EntryItem: React.FC<{ entry: Entry; cover?: boolean }> = ({
  entry,
  cover,
}) => (
  <li className="entry">
    <div>
      {entry.kicker && <p className="kicker">{entry.kicker}</p>}
      <h4>
        {entry.link ? (
          <External href={entry.link}>{entry.title}</External>
        ) : (
          entry.title
        )}
      </h4>
      {entry.detail && <p className="detail">{entry.detail}</p>}
    </div>
    {entry.image && (
      <img
        className={cover ? "thumb cover" : "thumb"}
        src={entry.image}
        alt=""
        loading="lazy"
      />
    )}
  </li>
);

const EntryList: React.FC<{
  entries: Entry[];
  visible?: number;
  noun?: string;
  cover?: boolean;
}> = ({ entries, visible = entries.length, noun, cover }) => {
  const rest = entries.slice(visible);
  return (
    <>
      <ul className="entries">
        {entries.slice(0, visible).map((entry) => (
          <EntryItem key={entry.title} entry={entry} cover={cover} />
        ))}
      </ul>
      {rest.length > 0 && (
        <details className="more">
          <summary>
            Show {rest.length} more {noun}
          </summary>
          <ul className="entries">
            {rest.map((entry) => (
              <EntryItem key={entry.title} entry={entry} cover={cover} />
            ))}
          </ul>
        </details>
      )}
    </>
  );
};

const Section: React.FC<{
  id: string;
  title: string;
  children: React.ReactNode;
}> = ({ id, title, children }) => (
  <section id={id} className="section">
    <h2>{title}</h2>
    <div className="section-body">{children}</div>
  </section>
);

const App = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      <header className="site-header">
        <div className="wrap">
          <a className="brand" href="#top">
            Rachel Fehily
          </a>
          <button
            className="menu-button"
            type="button"
            aria-label="Menu"
            aria-expanded={menuOpen}
            aria-controls="site-nav"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d={
                  menuOpen ? "M6 6l12 12M18 6L6 18" : "M4 7h16M4 12h16M4 17h16"
                }
              />
            </svg>
          </button>
          <nav
            id="site-nav"
            aria-label="Sections"
            className={menuOpen ? "open" : undefined}
            onClick={() => setMenuOpen(false)}
          >
            {nav.map(([id, label]) => (
              <a key={id} href={`#${id}`}>
                {label}
              </a>
            ))}
          </nav>
        </div>
      </header>

      <main id="top" className="wrap">
        <div className="hero">
          <div>
            <h1>Writer, PhD Scholar, Public Speaker</h1>
            <p className="lead">
              Rachel Fehily is a Research Ireland doctoral scholar at University
              College Dublin, where she lectured, workshopped and taught a broad
              range of subjects at the Schools of English, Drama, Film and
              Music.
            </p>
            <p className="lead">
              Her work, in the form of books, articles, spoken word pieces,
              conference lectures and film, has been widely published, broadcast
              and performed both nationally and internationally. Before pivoting
              to writing and academia, Rachel practised as a barrister for over
              twenty years in the areas of Criminal, Family and Tort Law,
              Redress and Mediation. She holds a BA from Trinity College Dublin,
              a BL from The King’s Inns, and an MA in Drama and Performance
              Studies from UCD.
            </p>
            <p className="hero-links">
              <a className="button" href={`mailto:${email}`}>
                Email Rachel
              </a>
              {profiles.slice(0, 3).map((profile) => (
                <External key={profile.name} href={profile.link}>
                  {profile.name}
                </External>
              ))}
            </p>
          </div>
          <img className="portrait" src={portrait} alt="Rachel Fehily" />
        </div>

        <ul className="logos" aria-label="Affiliations and publishers">
          {logos.map((logo) => (
            <li key={logo.name}>
              <img
                className={logo.large ? "large" : undefined}
                src={logo.image}
                alt={logo.name}
                loading="lazy"
              />
            </li>
          ))}
        </ul>

        <Section id="research" title="Research">
          <p className="prose">
            Rachel’s PhD at University College Dublin,{" "}
            <i>
              Adoption, An Interdisciplinary Reframing: Memoir, Legality and
              Multi-racialism
            </i>
            , is funded by a Government of Ireland Postgraduate Research
            Scholarship from Research Ireland. It takes the form of a memoir
            with the working title <i>From the Reject Room</i>, about growing up
            mixed-race and adopted in a literary quarter of Dublin, with a
            critical reflection on the memoir-making process. In 2026 she was a{" "}
            <External href="https://oclw.web.ox.ac.uk/people/rachel-fehily">
              Visiting Doctoral Student
            </External>{" "}
            at the Oxford Centre for Life-Writing, Wolfson College, University
            of Oxford.
          </p>
          <h3>Recent Publications</h3>
          <EntryList entries={publications} />
          <h3>Conference papers</h3>
          <EntryList
            entries={conferences}
            visible={3}
            noun="conference papers"
          />
          <h3>Public lectures</h3>
          <EntryList entries={talks} visible={3} noun="lectures" />
          <h3>Teaching</h3>
          <EntryList entries={teaching} />
        </Section>

        <Section id="books" title="Books">
          <ul className="books">
            {books.map((book) => (
              <li key={book.title}>
                <External href={book.link}>
                  <img src={book.image} alt="" loading="lazy" />
                  <span>{book.title}</span>
                </External>
              </li>
            ))}
          </ul>
          <h3>Contributions</h3>
          <EntryList entries={contributions} cover />
        </Section>

        <Section id="articles" title="Articles">
          <EntryList entries={articles} visible={5} noun="articles" />
        </Section>

        <Section id="film-radio" title="Film and radio">
          <h3>Radio</h3>
          <ul className="radio">
            {radio.map((show) => (
              <li key={show.title}>
                <External href={show.link}>
                  <img src={show.image} alt="" loading="lazy" />
                  <span>{show.title}</span>
                </External>
              </li>
            ))}
          </ul>
          <h3>Film and podcast</h3>
          <EntryList entries={film} />
        </Section>

        <Section id="about" title="About">
          <p className="prose">
            Rachel Fehily is a writer, public speaker, radio performer,
            university teacher and Research Ireland doctoral scholar at
            University College Dublin. She was a Visiting Doctoral Student at
            Wolfson College, University of Oxford, in 2026. Before turning to
            writing and academia she practised as a barrister for over twenty
            years in criminal, family and tort law, redress and mediation. She
            holds a BA in Economic and Social Studies from Trinity College
            Dublin, a BL from The King’s Inns and an MA in Drama and Performance
            Studies from UCD.
          </p>
          <p className="prose">
            As a barrister she represented clients in criminal trials, family
            law cases and before the Residential Institutions Redress Board, and
            she continues to give legal advice on a pro bono basis. She has
            taught at UCD since 2020 and curated the archive of the playwright
            Frank McGuinness for UCD’s James Joyce Library, writing the
            accompanying booklet. Her other work includes <i>The Hotel Room</i>,
            an event at the International Literature Festival Dublin on the
            difficulties faced by refugees living in Direct Provision, and a
            contribution to <i>The Belonging Project</i>, exhibited at the
            Museum of Literature Ireland (MoLI). She welcomes collaborative
            research, workshopping and teaching opportunities.
          </p>
          <h3>Memberships</h3>
          <p className="detail">
            Oxford Centre for Life-Writing research network, International
            Auto/Biography Association, Oral History Network of Ireland,
            Adoption Research Network of Ireland, Irish Network for Legal
            Humanities, and the Transnationalising the Humanities PhD Network at
            UCD.
          </p>
          <h3>Contact</h3>
          <p className="contact">
            <a href={`mailto:${email}`}>{email}</a>
          </p>
          <p className="contact">
            {profiles.map((profile) => (
              <External key={profile.name} href={profile.link}>
                {profile.name}
              </External>
            ))}
          </p>
        </Section>
      </main>

      <footer className="site-footer">
        <div className="wrap">
          <span>Rachel Fehily</span>
          <a href={`mailto:${email}`}>{email}</a>
        </div>
      </footer>
    </>
  );
};

export default App;
